import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Book } from "../data/books";
import TermsAndConditions from './TermsAndConditions';

interface BookCardProps {
  book: Book;
}

// Payment configuration for books with quantity purchase functionality
const paymentConfig = {
  omsorg: {
    1: {
      url: "https://buy.stripe.com/7sIaIhgR95xXgLeeUU",
      price: 149.95
    },
    2: {
      url: "https://buy.stripe.com/7sI6s1dEXaSh66A3cd",
      price: 299.90
    },
    3: {
      url: "https://buy.stripe.com/8wM7w5gR94tT9iM4gi",
      price: 449.85
    },
    4: {
      url: "https://buy.stripe.com/eVabMleJ1bWl9iMfZ1",
      price: 599.80
    }
  },
  toerst: {
    1: {
      url: "https://buy.stripe.com/8x2eVeb4faoJbW14JwgMw04",
      price: 49.00
    },
    2: {
      url: "https://buy.stripe.com/28E3cw8W72Wh1hn1xkgMw06",
      price: 98.00
    },
    3: {
      url: "https://buy.stripe.com/14AdRa3BN68taRXek6gMw07",
      price: 147.00
    },
    4: {
      url: "https://buy.stripe.com/5kQfZia0b9kFbW1foagMw08",
      price: 196.00
    }
  },
  bookofcomfort: {
    1: {
      url: "https://buy.stripe.com/8x214odcn0O9d052BogMw09",
      price: 70.00
    },
    2: {
      url: "https://buy.stripe.com/cNi14oc8jfJ3aRXcbYgMw0a",
      price: 140.00
    },
    3: {
      url: "https://buy.stripe.com/7sY8wQ8W71Sd0dj7VIgMw0b",
      price: 210.00
    },
    4: {
      url: "https://buy.stripe.com/aFa9AU5JVdAV1hndg2gMw0c",
      price: 280.00
    }
  }
} as { [bookId: string]: { [key: number]: { url: string; price: number } } };

const getShippingCost = (bookId: string, quantity: number): number => {
  // Tørst has flat 30 kr shipping regardless of quantity
  if (bookId === 'toerst') return 30;

  // Book of Comfort and Omsorg have variable shipping
  if (quantity <= 2) return 30;
  return 50;
};

const BookCard = ({ book }: BookCardProps) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState<number>(1);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  const isOmsorgBook = book.id === 'omsorg';
  const isToerstBook = book.id === 'toerst';
  const isBookOfComfort = book.id === 'bookofcomfort';
  const hasQuantityPurchase = isOmsorgBook || isToerstBook || isBookOfComfort;

  const handlePurchase = () => {
    if (hasQuantityPurchase && termsAccepted) {
      window.open(paymentConfig[book.id][quantity].url, '_blank');
    } else if (book.purchaseUrl && termsAccepted) {
      window.open(book.purchaseUrl, '_blank');
    }
  };

  const handleFreeEbook = () => {
    window.location.href = '/bookofcomfort';
  };

  const handleShowInterest = () => {
    const subject = encodeURIComponent(`Interesse for bogen: ${t(`books.${book.id}.title`)}`);
    const body = encodeURIComponent(`Hej,\n\nJeg vil gerne vise interesse for bogen "${t(`books.${book.id}.title`)}" af Josef Kristensen.\n\nVenlig hilsen`);
    window.open(`mailto:info@omsorgsterapi.dk?subject=${subject}&body=${body}`, '_blank');
  };

  const shippingCost = getShippingCost(book.id, quantity);
  const currentPrice = hasQuantityPurchase ? paymentConfig[book.id][quantity].price : book.price;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="flex flex-col lg:flex-row gap-4 p-4">
        {/* 1. Book Cover */}
        <div className="lg:w-1/5 flex justify-center lg:justify-start">
          <div className="w-full max-w-[120px] lg:max-w-none">
            <AspectRatio ratio={2 / 3} className="bg-gray-100 rounded-lg overflow-hidden shadow-md">
              <img
                src={book.coverImage}
                alt={`${book.title} - forside`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/placeholder.svg";
                }}
              />
            </AspectRatio>
          </div>
        </div>

        {/* 2. Book Text Info */}
        <div className="lg:w-1/4 flex flex-col">

          {/* Book Info */}
          <div className="text-center lg:text-left mb-4">
            <h4 className="text-lg font-bold text-[#006B5F] mb-1">
              {t(`books.${book.id}.title`)}
            </h4>
            <p className="text-sm text-gray-700 italic mb-2">
              {t(`books.${book.id}.subtitle`)}
            </p>
            <p className="text-xs text-gray-600 mb-3">
              {t('books.common.by')} {book.author}
            </p>
          </div>

          {/* Description */}
          <div className="flex-grow">
            <p className="text-gray-700 text-xs leading-relaxed mb-3">
              {t(`books.${book.id}.aboutText1`).length > 150
                ? `${t(`books.${book.id}.aboutText1`).substring(0, 150)}...`
                : t(`books.${book.id}.aboutText1`)
              }
            </p>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 font-medium">
                {t(`books.${book.id}.pages`)}
              </p>
              <p className="text-xs text-gray-500 font-medium">
                {book.id.startsWith('bookofcomfort') ? t('books.common.languageEnglish') : t('books.common.language')}
              </p>
            </div>
          </div>
        </div>

        {/* 3. Author's Inspiration */}
        <div className="lg:w-1/3 flex flex-col">
          <div className="p-3 bg-gray-50 rounded-lg h-full">
            <h5 className="text-sm font-bold text-[#006B5F] mb-2">
              {t(`books.${book.id}.inspiration`)}
            </h5>
            <blockquote className="text-xs text-gray-700 italic leading-relaxed">
              {t(`books.${book.id}.inspirationText`).length > 200
                ? `${t(`books.${book.id}.inspirationText`).substring(0, 200)}...`
                : t(`books.${book.id}.inspirationText`)
              }
              <footer className="mt-2 font-bold not-italic">
                - {t(`books.${book.id}.inspirationAuthor`)}
              </footer>
            </blockquote>
          </div>
        </div>

        {/* 4. Purchase Functionality */}
        <div className="lg:w-1/5 flex flex-col justify-center">
          <div className="text-center">
            {book.soldOut ? (
              /* Sold Out Section */
              <div className="space-y-2 opacity-60">
                <p className="text-gray-600 font-bold text-xs text-center">
                  {t('books.common.soldOut')}
                </p>
                <Button
                  onClick={handleShowInterest}
                  className="bg-[#006B5F] hover:bg-[#005A4F] text-white font-medium py-2 px-3 rounded w-full text-xs opacity-80 hover:opacity-100 transition-opacity"
                >
                  {t('books.common.showInterest')}
                </Button>
              </div>
            ) : book.available && (hasQuantityPurchase || book.purchaseUrl) ? (
              <div className="space-y-3">
                {/* Quantity Selector - for books with quantity purchase */}
                {hasQuantityPurchase && (
                  <div className="flex justify-center">
                    <Select
                      value={quantity.toString()}
                      onValueChange={(value) => setQuantity(parseInt(value))}
                    >
                      <SelectTrigger className="w-16 text-xs h-7">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Price */}
                {currentPrice !== undefined && currentPrice > 0 && (
                  <div className="text-center">
                    <p className="text-sm font-bold text-[#006B5F]">
                      {currentPrice.toFixed(2)} DKK
                    </p>
                    {hasQuantityPurchase && (
                      <p className="text-xs text-gray-500">+ {shippingCost} kr</p>
                    )}
                  </div>
                )}

                {/* Terms & Conditions */}
                <div className="flex items-center justify-center space-x-1">
                  <Checkbox
                    id={`terms-${book.id}`}
                    checked={termsAccepted}
                    onCheckedChange={(checked) => {
                      setTermsAccepted(checked as boolean);
                    }}
                    className="scale-75"
                  />
                  <Label
                    htmlFor={`terms-${book.id}`}
                    className="text-xs font-normal leading-tight text-gray-700 cursor-pointer"
                  >
                    {t('book.terms')} <TermsAndConditions />
                  </Label>
                </div>

                {/* Buy Button */}
                <Button
                  onClick={handlePurchase}
                  className="bg-[#006B5F] hover:bg-[#005A4F] text-white font-medium py-2 px-3 rounded w-full text-xs"
                  disabled={!termsAccepted}
                >
                  {t('book.buy')}
                </Button>

                {/* Book of Comfort - Free E-book Button */}
                {isBookOfComfort && (
                  <Button
                    onClick={handleFreeEbook}
                    className="bg-white hover:bg-gray-50 text-[#006B5F] border-2 border-[#006B5F] font-medium py-2 px-3 rounded w-full text-xs"
                  >
                    {t('book.freeEbook')}
                  </Button>
                )}

                {/* Bulk purchase info - for books with quantity purchase */}
                {hasQuantityPurchase && (
                  <p className="text-xs text-gray-500 text-center leading-tight">
                    {t('book.bulkText')}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-xs text-center">
                {t('books.common.comingSoon')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookCard;
